/* eslint-disable no-restricted-imports -- this wrapper is the single canonical entry point for dayjs; all consumers must import from `@common/services/datetime`. */
import dayjs, { type Dayjs as DatetimeType, type PluginFunc } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';

// NOTE: this wrapper reproduces moment's semantics on top of dayjs. See
// .ai/context/decisions/023-datetime-moment-parity.md for the contract.

// NOTE: order matters. advancedFormat must extend before utc, and the two
// mode plugins at the end of this file after timezone.
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(localeData);
// NOTE: dayjs gates moment's L/LL/LT tokens behind this plugin and renders an
// unknown token as a literal, so without it `format('L')` yields "L".
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(weekday);

const MOMENT_DURATION_THRESHOLDS = {
  ss: 44,
  s: 45,
  m: 45,
  h: 22,
  d: 26,
  M: 11,
};
const DURATION_DAYS_PER_MONTH = 146097 / 4800;
const DURATION_RELATIVE_TIME = {
  future: 'in %s',
  past: '%s ago',
  s: 'a few seconds',
  ss: '%d seconds',
  m: 'a minute',
  mm: '%d minutes',
  h: 'an hour',
  hh: '%d hours',
  d: 'a day',
  dd: '%d days',
  M: 'a month',
  MM: '%d months',
  y: 'a year',
  yy: '%d years',
} as const;

function momentParityHumanize(
  this: { asMilliseconds: () => number },
  withSuffix?: boolean
): string {
  const ms = this.asMilliseconds();
  const abs = Math.abs(ms);
  const days = abs / 86_400_000;
  const { round } = Math;
  const seconds = round(abs / 1_000);
  const minutes = round(abs / 60_000);
  const hours = round(abs / 3_600_000);
  const roundedDays = round(days);
  const months = round(days / DURATION_DAYS_PER_MONTH);
  const years = round(days / DURATION_DAYS_PER_MONTH / 12);
  const th = MOMENT_DURATION_THRESHOLDS;

  const bucket: [keyof typeof DURATION_RELATIVE_TIME, number?] =
    seconds <= th.ss
      ? ['s']
      : seconds < th.s
        ? ['ss', seconds]
        : minutes <= 1
          ? ['m']
          : minutes < th.m
            ? ['mm', minutes]
            : hours <= 1
              ? ['h']
              : hours < th.h
                ? ['hh', hours]
                : roundedDays <= 1
                  ? ['d']
                  : roundedDays < th.d
                    ? ['dd', roundedDays]
                    : months <= 1
                      ? ['M']
                      : months < th.M
                        ? ['MM', months]
                        : years <= 1
                          ? ['y']
                          : ['yy', years];

  const [key, value] = bucket;
  const phrase = DURATION_RELATIVE_TIME[key].replace('%d', String(value));
  if (!withSuffix) {
    return phrase;
  }
  const direction =
    ms > 0 ? DURATION_RELATIVE_TIME.future : DURATION_RELATIVE_TIME.past;
  return direction.replace('%s', phrase);
}

Object.getPrototypeOf(dayjs.duration(0)).humanize = momentParityHumanize;

const ISO_OFFSET_SUFFIX = /([+-])(\d{2}):?(\d{2})?$/;

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/;

function hasOverflowDatePortion(input: string): boolean {
  const match = input.match(ISO_DATE_PREFIX);
  if (!match) {
    return false;
  }
  return !dayjs(match[1], 'YYYY-MM-DD', true).isValid();
}

// NOTE: moment returns null from `toISOString()` on an invalid date and dayjs
// throws a RangeError, so the string-typed return deliberately yields null.
const invalidToISOStringParity: PluginFunc = (_option, DayjsClass) => {
  const originalToISOString = DayjsClass.prototype.toISOString;
  DayjsClass.prototype.toISOString = function toISOString(
    this: DatetimeType
  ): string {
    return this.isValid()
      ? originalToISOString.call(this)
      : (null as unknown as string);
  };
};
dayjs.extend(invalidToISOStringParity);

// NOTE: scanning the whole string reads a bare date's trailing `-DD` as an
// offset, so only the portion after the date is searched.
function isoOffsetMatch(input: string): RegExpMatchArray | null {
  const timeSeparator = input.search(/[T ]/);
  if (timeSeparator === -1) {
    return null;
  }
  return input.slice(timeSeparator).match(ISO_OFFSET_SUFFIX);
}

function hasExplicitZone(input: string): boolean {
  return input.endsWith('Z') || isoOffsetMatch(input) !== null;
}

const MS_PER_MINUTE = 60_000;

type DatetimeExtensionBag = {
  $fixedOffset?: number;
  $zoneAware?: string;
  $timezone?: string;
  $zoneOffset?: number;
};

// NOTE: `$d`, `$L`, `$x` and the `(date, config)` builder below are dayjs
// internals. `$d` holds the DISPLAY clock in both modes, not the instant.
type FixedOffsetInstance = DatetimeType & {
  $d: Date;
  $L: string;
  $x?: DatetimeExtensionBag;
};

type FixedOffsetPrototype = {
  utcOffset: (
    input?: number | string,
    keepLocalTime?: boolean
  ) => number | DatetimeType;
  valueOf: () => number;
  format: (template?: string) => string;
  startOf: (unit: string, isStartOf?: boolean) => DatetimeType;
};

const buildDatetime = dayjs as unknown as (
  date: Date,
  config: { utc?: boolean; locale?: string; x?: DatetimeExtensionBag }
) => DatetimeType;

function fixedOffsetOf(instance: unknown): number | undefined {
  return (instance as FixedOffsetInstance).$x?.$fixedOffset;
}

function fixedOffsetDatetime(
  instantMs: number,
  offsetMinutes: number,
  locale?: string
): DatetimeType {
  if (offsetMinutes === 0) {
    return buildDatetime(new Date(instantMs), { utc: true, locale });
  }
  return buildDatetime(new Date(instantMs + offsetMinutes * MS_PER_MINUTE), {
    utc: true,
    locale,
    x: { $fixedOffset: offsetMinutes },
  });
}

const fixedOffsetMode: PluginFunc = (_option, DayjsClass) => {
  const proto = DayjsClass.prototype as unknown as FixedOffsetPrototype;
  const previousUtcOffset = proto.utcOffset;
  const previousValueOf = proto.valueOf;
  const previousFormat = proto.format;
  const previousStartOf = proto.startOf;

  const withoutMarker = (
    instance: FixedOffsetInstance,
    epochMs: number
  ): DatetimeType =>
    buildDatetime(new Date(epochMs), { utc: true, locale: instance.$L });

  proto.utcOffset = function utcOffset(
    this: FixedOffsetInstance,
    input?: number | string,
    keepLocalTime?: boolean
  ): number | DatetimeType {
    const offset = fixedOffsetOf(this);
    if (offset !== undefined && input === undefined) {
      return offset;
    }
    return previousUtcOffset.call(this, input, keepLocalTime);
  };

  proto.valueOf = function valueOf(this: FixedOffsetInstance): number {
    const offset = fixedOffsetOf(this);
    if (offset === undefined) {
      return previousValueOf.call(this);
    }
    return this.$d.valueOf() - offset * MS_PER_MINUTE;
  };

  proto.format = function format(
    this: FixedOffsetInstance,
    template?: string
  ): string {
    const offset = fixedOffsetOf(this);
    // NOTE: the utc plugin's default template embeds `[Z]` as a LITERAL, so a
    // non-zero offset needs the template passed explicitly to render its label.
    if (offset !== undefined && !template) {
      return previousFormat.call(this, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    return previousFormat.call(this, template);
  };

  proto.startOf = function startOf(
    this: FixedOffsetInstance,
    unit: string,
    isStartOf?: boolean
  ): DatetimeType {
    const offset = fixedOffsetOf(this);
    if (offset === undefined) {
      return previousStartOf.call(this, unit, isStartOf);
    }
    const displayClock = withoutMarker(this, this.$d.valueOf());
    const truncated = previousStartOf.call(
      displayClock,
      unit,
      isStartOf
    ) as FixedOffsetInstance;
    return fixedOffsetDatetime(
      truncated.$d.valueOf() - offset * MS_PER_MINUTE,
      offset,
      this.$L
    );
  };
};
dayjs.extend(fixedOffsetMode);

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zoneFormatter(zone: string): Intl.DateTimeFormat {
  const cached = zoneFormatters.get(zone);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour12: false,
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  zoneFormatters.set(zone, formatter);
  return formatter;
}

function zoneOffsetAt(instantMs: number, zone: string): number {
  const fields: Record<string, number> = Object.fromEntries(
    zoneFormatter(zone)
      .formatToParts(new Date(instantMs))
      .map(({ type, value }) => [type, parseInt(value, 10)])
  );
  // NOTE: `hour12: false` renders midnight as hour 24 in some engines.
  const hour = fields.hour === 24 ? 0 : fields.hour;
  const wallClock = `${fields.year}-${fields.month}-${fields.day} ${hour}:${fields.minute}:${fields.second}:000`;
  // NOTE: floor, not `% 1000`. The remainder carries the dividend's sign, so
  // a pre-1970 instant with milliseconds would yield a non-integer offset.
  const wholeSeconds = Math.floor(instantMs / 1000) * 1000;
  return (dayjs.utc(wallClock).valueOf() - wholeSeconds) / MS_PER_MINUTE;
}

function instantForWallClock(wallMs: number, zone: string): number {
  const probed = zoneOffsetAt(wallMs, zone);
  const candidate = wallMs - probed * MS_PER_MINUTE;
  const atCandidate = zoneOffsetAt(candidate, zone);
  if (atCandidate === probed) {
    return candidate;
  }
  const alternate = wallMs - atCandidate * MS_PER_MINUTE;
  if (zoneOffsetAt(alternate, zone) === atCandidate) {
    return alternate;
  }
  return wallMs - Math.min(probed, atCandidate) * MS_PER_MINUTE;
}

function buildZoneAware(
  wallMs: number,
  zone: string,
  offsetMinutes: number,
  locale?: string
): DatetimeType {
  return buildDatetime(new Date(wallMs), {
    utc: true,
    locale,
    x: { $zoneAware: zone, $timezone: zone, $zoneOffset: offsetMinutes },
  });
}

function zoneAwareDatetime(
  instantMs: number,
  zone: string,
  locale?: string
): DatetimeType {
  const offset = zoneOffsetAt(instantMs, zone);
  return buildZoneAware(
    instantMs + offset * MS_PER_MINUTE,
    zone,
    offset,
    locale
  );
}

// NOTE: on the second pass of a fall-back hour both offsets are valid at that
// wall clock, so honoring the caller's keeps `startOf` off the first pass.
function zoneAwareFromWallClock(
  wallMs: number,
  zone: string,
  locale?: string,
  preferredOffset?: number
): DatetimeType {
  if (
    preferredOffset !== undefined &&
    zoneOffsetAt(wallMs - preferredOffset * MS_PER_MINUTE, zone) ===
      preferredOffset
  ) {
    return buildZoneAware(wallMs, zone, preferredOffset, locale);
  }
  return zoneAwareDatetime(instantForWallClock(wallMs, zone), zone, locale);
}

// NOTE: moment moves the wall clock for these units and the instant for every
// other one, and across a DST transition the two land an hour apart.
const CALENDAR_UNITS = new Set(['date', 'day', 'week', 'month', 'year']);

const UTC_OFFSET_STRING = /[+-]\d\d(?::?\d\d)?/;
const UTC_OFFSET_PARTS = /([+-]|\d\d)/g;

function offsetMinutesFromString(input: string): number | null {
  const match = input.match(UTC_OFFSET_STRING);
  if (!match) {
    return null;
  }
  const parts = match[0].match(UTC_OFFSET_PARTS) ?? ['-', '0', '0'];
  const magnitude = 60 * Number(parts[1]) + Number(parts[2]);
  if (magnitude === 0) {
    return 0;
  }
  return parts[0] === '+' ? magnitude : -magnitude;
}

type ZoneAwareBag = {
  $zoneAware: string;
  $timezone: string;
  $zoneOffset: number;
};

function zoneAwareBag(instance: FixedOffsetInstance): ZoneAwareBag | undefined {
  const bag = instance.$x;
  return bag?.$zoneAware === undefined ? undefined : (bag as ZoneAwareBag);
}

type ZoneAwarePrototype = {
  utcOffset: (
    input?: number | string,
    keepLocalTime?: boolean
  ) => number | DatetimeType;
  valueOf: () => number;
  format: (template?: string) => string;
  startOf: (unit: string, isStartOf?: boolean) => DatetimeType;
  add: (value: number, unit?: string) => DatetimeType;
  set: (unit: string, value: number) => DatetimeType;
  tz: (zone?: string, keepLocalTime?: boolean) => DatetimeType;
  diff: (input?: unknown, unit?: string, asFloat?: boolean) => number;
  isUTC: () => boolean;
  $utils: () => { p: (unit?: string) => string };
};

const zoneAwareMode: PluginFunc = (_option, DayjsClass) => {
  const proto = DayjsClass.prototype as unknown as ZoneAwarePrototype;
  const previousUtcOffset = proto.utcOffset;
  const previousValueOf = proto.valueOf;
  const previousFormat = proto.format;
  const previousStartOf = proto.startOf;
  const previousAdd = proto.add;
  const previousSet = proto.set;
  const previousTz = proto.tz;
  const previousDiff = proto.diff;
  // NOTE: `$utils().p` is dayjs's own unit normalizer, so this mode's unit
  // families cannot disagree with core's dispatch. Stateless, hence unbound.
  const normalizeUnit = proto.$utils().p;

  // NOTE: this bare copy carries no `$x`, so every override here early-returns
  // on it. Otherwise core's `add` re-enters this prototype through `set`.
  const bareWallClock = (instance: FixedOffsetInstance): DatetimeType =>
    buildDatetime(new Date(instance.$d.valueOf()), {
      utc: true,
      locale: instance.$L,
    });

  proto.utcOffset = function utcOffset(
    this: FixedOffsetInstance,
    input?: number | string,
    keepLocalTime?: boolean
  ): number | DatetimeType {
    const bag = zoneAwareBag(this);
    if (input === undefined) {
      return bag === undefined ? previousUtcOffset.call(this) : bag.$zoneOffset;
    }
    if (!this.isValid()) {
      return previousUtcOffset.call(this, input, keepLocalTime);
    }
    const requested =
      typeof input === 'string' ? offsetMinutesFromString(input) : input;
    if (requested === null) {
      return this;
    }
    const minutes = Math.abs(requested) <= 16 ? requested * 60 : requested;
    if (keepLocalTime) {
      const wallClock = dayjs
        .utc(this.format('YYYY-MM-DDTHH:mm:ss.SSS'))
        .valueOf();
      return fixedOffsetDatetime(
        wallClock - minutes * MS_PER_MINUTE,
        minutes,
        this.$L
      );
    }
    return fixedOffsetDatetime(this.valueOf(), minutes, this.$L);
  };

  proto.valueOf = function valueOf(this: FixedOffsetInstance): number {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousValueOf.call(this);
    }
    return this.$d.valueOf() - bag.$zoneOffset * MS_PER_MINUTE;
  };

  proto.format = function format(
    this: FixedOffsetInstance,
    template?: string
  ): string {
    const bag = zoneAwareBag(this);
    if (bag !== undefined && !template && bag.$zoneOffset !== 0) {
      return previousFormat.call(this, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    return previousFormat.call(this, template);
  };

  proto.startOf = function startOf(
    this: FixedOffsetInstance,
    unit: string,
    isStartOf?: boolean
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousStartOf.call(this, unit, isStartOf);
    }
    const truncated = previousStartOf.call(
      bareWallClock(this),
      unit,
      isStartOf
    ) as FixedOffsetInstance;
    return zoneAwareFromWallClock(
      truncated.$d.valueOf(),
      bag.$zoneAware,
      this.$L,
      bag.$zoneOffset
    );
  };

  proto.add = function add(
    this: FixedOffsetInstance,
    value: number,
    unit?: string
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousAdd.call(this, value, unit);
    }
    const advanced = previousAdd.call(
      bareWallClock(this),
      value,
      unit
    ) as FixedOffsetInstance;
    if (CALENDAR_UNITS.has(normalizeUnit(unit))) {
      return zoneAwareFromWallClock(
        advanced.$d.valueOf(),
        bag.$zoneAware,
        this.$L,
        bag.$zoneOffset
      );
    }
    return zoneAwareDatetime(
      this.valueOf() + (advanced.$d.valueOf() - this.$d.valueOf()),
      bag.$zoneAware,
      this.$L
    );
  };

  proto.set = function set(
    this: FixedOffsetInstance,
    unit: string,
    value: number
  ): DatetimeType {
    const bag = zoneAwareBag(this);
    if (bag === undefined) {
      return previousSet.call(this, unit, value);
    }
    const updated = previousSet.call(
      bareWallClock(this),
      unit,
      value
    ) as FixedOffsetInstance;
    return zoneAwareFromWallClock(
      updated.$d.valueOf(),
      bag.$zoneAware,
      this.$L,
      bag.$zoneOffset
    );
  };

  proto.tz = function tz(
    this: FixedOffsetInstance,
    zone?: string,
    keepLocalTime?: boolean
  ): DatetimeType {
    if (!this.isValid()) {
      return previousTz.call(this, zone, keepLocalTime);
    }
    const target = zone ?? dayjs.tz.guess();
    if (keepLocalTime) {
      return zoneAwareFromWallClock(
        dayjs.utc(this.format('YYYY-MM-DDTHH:mm:ss.SSS')).valueOf(),
        target,
        this.$L
      );
    }
    return zoneAwareDatetime(this.valueOf(), target, this.$L);
  };

  proto.diff = function diff(
    this: FixedOffsetInstance,
    input?: unknown,
    unit?: string,
    asFloat?: boolean
  ): number {
    // NOTE: the gate is UTC mode, not the mode marker. `parseZone` hands back
    // a bare `dayjs.utc` for `Z`-suffixed and zoneless input, which carries none.
    if (!this.isUTC()) {
      return previousDiff.call(this, input, unit, asFloat);
    }
    const instant = dayjs(input as dayjs.ConfigType).valueOf();
    if (Number.isNaN(instant)) {
      return previousDiff.call(this, input, unit, asFloat);
    }
    const bag = zoneAwareBag(this);
    const fixed = fixedOffsetOf(this);
    const comparand =
      bag === undefined
        ? fixedOffsetDatetime(instant, fixed ?? 0, this.$L)
        : zoneAwareDatetime(instant, bag.$timezone, this.$L);
    return previousDiff.call(this, comparand, unit, asFloat);
  };
};
dayjs.extend(zoneAwareMode);

export function parseZone(input: string | DatetimeType): DatetimeType {
  if (isDatetime(input)) {
    return input;
  }
  if (typeof input !== 'string') {
    return dayjs(NaN);
  }

  if (hasOverflowDatePortion(input)) {
    return dayjs(NaN);
  }

  if (input.endsWith('Z')) {
    return dayjs.utc(input);
  }

  const match = isoOffsetMatch(input);
  // NOTE: a zoneless string is read as UTC to match `moment.parseZone`.
  // `dayjs(input)` would read it in the host zone, same wall clock, new instant.
  if (!match) {
    return dayjs.utc(input);
  }

  const sign = match[1] === '+' ? 1 : -1;
  const hours = parseInt(match[2], 10);
  const minutes = parseInt(match[3] || '0', 10);
  const totalMinutes = sign * (hours * 60 + minutes);

  const wallClock = input.slice(0, input.length - match[0].length);
  const displayMs = dayjs.utc(wallClock).valueOf();
  return fixedOffsetDatetime(
    displayMs - totalMinutes * MS_PER_MINUTE,
    totalMinutes
  );
}

export function isDatetime(value: unknown): value is DatetimeType {
  return dayjs.isDayjs(value);
}

function isMomentObject(
  value: unknown
): value is { format: (template?: string) => string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { _isAMomentObject?: boolean })._isAMomentObject === true
  );
}

function isLocalModeMoment(value: unknown): boolean {
  if (!isMomentObject(value)) {
    return false;
  }
  const probe = value as unknown as { isLocal?: () => boolean };
  return typeof probe.isLocal === 'function' && probe.isLocal();
}

function momentInstant(value: {
  format: (template?: string) => string;
}): number {
  return (value as unknown as { valueOf: () => number }).valueOf();
}

// NOTE: moment's default `.format()` carries no milliseconds, so this file and
// `moment-bridge.ts` must both round-trip a moment through this exact template.
export const MOMENT_ROUNDTRIP_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

// NOTE: `Object.prototype.toString` matches a Date from another realm, which
// `instanceof` alone misses. `moment.isDate` tests the same string.
function isDateObject(value: unknown): value is Date {
  return Object.prototype.toString.call(value) === '[object Date]';
}

function hasNoExtraArgs(rest: unknown[]): boolean {
  return rest.every((arg) => arg === undefined);
}

function createDatetime(
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType {
  if (isMomentObject(date)) {
    if (isLocalModeMoment(date)) {
      return (dayjs as (...args: unknown[]) => DatetimeType)(
        momentInstant(date)
      );
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT));
  }
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return (dayjs as (...args: unknown[]) => DatetimeType)(date);
  }
  if (
    hasNoExtraArgs(rest) &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs(NaN);
  }
  return (dayjs as (...args: unknown[]) => DatetimeType)(date, ...rest);
}

const createUtcDatetime = ((
  date?: dayjs.ConfigType,
  ...rest: unknown[]
): DatetimeType => {
  if (isMomentObject(date)) {
    if (isLocalModeMoment(date)) {
      return dayjs(momentInstant(date)).utc();
    }
    return parseZone(date.format(MOMENT_ROUNDTRIP_FORMAT)).utc();
  }
  if (!hasNoExtraArgs(rest) && isDateObject(date)) {
    return dayjs.utc(date);
  }
  if (
    rest.length === 0 &&
    typeof date === 'string' &&
    hasOverflowDatePortion(date)
  ) {
    return dayjs.utc(NaN);
  }
  return (dayjs.utc as (...args: unknown[]) => DatetimeType)(date, ...rest);
}) as typeof dayjs.utc;

const createTzDatetime = Object.assign(
  ((input?: dayjs.ConfigType, zone?: string): DatetimeType => {
    if (
      typeof input === 'string' &&
      zone !== undefined &&
      !hasExplicitZone(input)
    ) {
      const wallClock = dayjs.utc(input);
      if (!wallClock.isValid()) {
        return wallClock;
      }
      return zoneAwareFromWallClock(wallClock.valueOf(), zone);
    }
    return createDatetime(input).tz(zone as string);
  }) as typeof dayjs.tz,
  dayjs.tz
);

export const datetime = Object.assign(createDatetime, dayjs, {
  utc: createUtcDatetime,
  tz: createTzDatetime,
}) as typeof dayjs;

export const todayIso = (): string => datetime().format('YYYY-MM-DD');

export type { DatetimeType };
